import { PlayCircle, ExternalLink } from 'lucide-react';
import { youtubeEmbedUrl } from './youtube-embed-url';

/**
 * Render a mission webcast: an inline player when the URL is an embeddable
 * YouTube stream, otherwise a labelled external link (e.g. X/Twitter streams,
 * which cannot be embedded).
 */
export function WebcastEmbed({ url, live }: { url: string; live: boolean }): React.JSX.Element | null {
  if (!url) return null;
  const embed = youtubeEmbedUrl(url);
  return embed ? <EmbeddedPlayer src={embed} live={live} /> : <ExternalWebcastLink url={url} live={live} />;
}

/** Inline YouTube player with a LIVE badge when streaming. */
function EmbeddedPlayer({ src, live }: { src: string; live: boolean }): React.JSX.Element {
  return (
    <div className="border-t border-echo-border">
      <div className="flex items-center gap-2 px-4 pt-3 text-[11px] uppercase tracking-wider text-muted-foreground">
        <PlayCircle className="h-3.5 w-3.5 text-echo-yellow" />
        Webcast
        {live && <LivePill />}
      </div>
      <div className="aspect-video w-full px-4 pb-3 pt-2">
        <iframe
          src={src}
          title="Mission webcast"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="h-full w-full rounded-md border border-echo-border"
        />
      </div>
    </div>
  );
}

/** External webcast link for non-embeddable hosts. */
function ExternalWebcastLink({ url, live }: { url: string; live: boolean }): React.JSX.Element {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex w-full items-center justify-center gap-2 border-t border-echo-border py-2.5 text-xs font-semibold uppercase tracking-wider text-echo-yellow transition-colors hover:bg-white/[0.05]"
    >
      <ExternalLink className="h-3.5 w-3.5" />
      Watch Live
      {live && <LivePill />}
    </a>
  );
}

/** Pulsing "LIVE" badge. */
function LivePill(): React.JSX.Element {
  return (
    <span className="flex items-center gap-1 rounded-full bg-red-500/20 px-2 py-0.5 text-[10px] font-bold text-red-400">
      <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
      LIVE
    </span>
  );
}
